import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createMarioSkillsIframeMessageHandler,
  isMarioSkillsShellPathname,
} from '@src/utils/mario-skills-embed';
import {
  getSkillsOfficialUrl,
  shouldOpenSkillsInNewTabForLocal,
} from '@src/utils/route';
import { isMobile } from '@src/utils/user-agent';

function navigateSkillsIframeHome(
  iframe: HTMLIFrameElement | null,
  url: string,
  search: string,
  hash: string,
) {
  if (!iframe) {
    return;
  }

  if (iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: 'mario-skills-navigate',
        path: '/skills-official',
        search,
        hash,
      },
      window.location.origin,
    );
    return;
  }

  iframe.src = url;
}

export default function SkillsPersistentIframe() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSkillsRoute = !isMobile && isMarioSkillsShellPathname(location.pathname);
  const redirectInLocal = shouldOpenSkillsInNewTabForLocal();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const previousPathnameRef = useRef(location.pathname);
  const initialUrlRef = useRef(
    isSkillsRoute && !redirectInLocal
      ? getSkillsOfficialUrl(location.pathname, location.search, location.hash)
      : '',
  );
  const [mounted, setMounted] = useState(isSkillsRoute && !redirectInLocal);

  useEffect(() => {
    if (isSkillsRoute && !redirectInLocal && !mounted) {
      initialUrlRef.current = getSkillsOfficialUrl(
        location.pathname,
        location.search,
        location.hash,
      );
      setMounted(true);
    }
  }, [
    isSkillsRoute,
    redirectInLocal,
    mounted,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const shouldResetToSkillsHome =
      location.pathname === '/skills' && previousPathname !== '/skills';

    if (mounted && !redirectInLocal && shouldResetToSkillsHome) {
      const nextUrl = getSkillsOfficialUrl('/skills', location.search, location.hash);
      initialUrlRef.current = nextUrl;
      navigateSkillsIframeHome(
        iframeRef.current,
        nextUrl,
        location.search,
        location.hash,
      );
    }

    previousPathnameRef.current = location.pathname;
  }, [
    location.pathname,
    location.search,
    location.hash,
    redirectInLocal,
    mounted,
  ]);

  useEffect(() => {
    const handleMessage = createMarioSkillsIframeMessageHandler(navigate);
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);

  if (!mounted) {
    return null;
  }

  return (
    <div
      className="skills-iframe-persistent"
      style={{ display: isSkillsRoute ? 'flex' : 'none' }}
      aria-hidden={!isSkillsRoute}
    >
      <iframe
        ref={iframeRef}
        className="skills-official-iframe"
        src={initialUrlRef.current}
        title="Skills Official"
      />
    </div>
  );
}
